import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2 } from 'lucide-react';
import ChecklistDialog from './ChecklistDialog';
import IssueDetailDialog from './IssueDetailDialog';

const PRIO = {
  1: { label: 'P1 Critique', cls: 'bg-red-600 text-white' },
  2: { label: 'P2 Urgent', cls: 'bg-orange-500 text-white' },
  3: { label: 'P3 Important', cls: 'bg-yellow-500 text-white' },
  4: { label: 'P4 Normal', cls: 'bg-blue-100 text-blue-700' },
};

const ageDays = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

const ATraiterTab = ({ vehicles, sites, fleetMe, onRefreshParc, onOpenAccident }) => {
  const [rows, setRows] = useState(null);
  const [checkVehicle, setCheckVehicle] = useState(null);
  const [issueId, setIssueId] = useState(null);
  const isFM = fleetMe?.is_fleet_manager;
  const siteName = (id) => (id ? (sites.find(s => s.site_id === id)?.name || id) : 'Flotte commune');

  const load = async () => {
    try {
      const [ctrls, issues, deadlines, accidents] = await Promise.all([
        axios.get(`${API}/fleet/controls/summary`, { withCredentials: true }).then(r => r.data).catch(() => null),
        axios.get(`${API}/fleet/issues?only_open=true`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/fleet/deadlines`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/fleet/accidents`, { withCredentials: true }).then(r => r.data).catch(() => []),
      ]);
      const out = [];
      const vmap = Object.fromEntries(vehicles.map(v => [v.vehicle_id, v]));
      // Véhicules immobilisés / en réparation
      vehicles.filter(v => ['immobilise', 'en_reparation'].includes(v.operational_status)).forEach(v => out.push({
        key: `immo_${v.vehicle_id}`, prio: 1, plate: v.plate_number, site: siteName(v.site_id),
        problem: v.operational_status === 'immobilise' ? 'Véhicule immobilisé' : 'Véhicule en réparation',
        age: null, owner: 'Chef de parc',
        action: isFM && v.blocking_issues_count === 0 ? { label: 'Remettre en service', run: () => returnToService(v) } : { label: 'Réparations en cours', run: null },
      }));
      // Demandes d'immobilisation + pannes
      issues.forEach(i => {
        const isRequest = i.immobilization_recommended && i.status === 'signalee';
        out.push({
          key: `iss_${i.issue_id}`, prio: i.severity === 'bloquant' ? 1 : i.severity === 'defectueux' ? 3 : 4,
          plate: i.plate_number, site: siteName(vmap[i.vehicle_id]?.site_id),
          problem: `${isRequest ? "Demande d'immobilisation — " : 'Panne — '}${i.item_label}`,
          age: ageDays(i.created_at), owner: i.assigned_to?.name || i.reported_by?.name || '—',
          action: { label: 'Ouvrir la panne', run: () => setIssueId(i.issue_id) },
        });
      });
      // Échéances urgentes / expirées
      deadlines.filter(d => ['expire', 'urgent'].includes(d.status)).forEach(d => out.push({
        key: `dl_${d.vehicle_id}_${d.type}`, prio: d.status === 'expire' ? 2 : 3,
        plate: d.plate_number, site: siteName(d.site_id),
        problem: `Échéance ${d.status === 'expire' ? 'EXPIRÉE' : 'urgente'} — ${d.label}${d.due_date ? ` (${d.due_date})` : ''}`,
        age: d.days_left != null && d.days_left < 0 ? -d.days_left : null, owner: 'Chef de parc',
        action: { label: 'Voir les échéances', run: () => window.dispatchEvent(new CustomEvent('fleet-goto-tab', { detail: 'deadlines' })) },
      }));
      // Accidents incomplets
      accidents.filter(a => !['clos'].includes(a.status) && (a.report_available !== 'disponible' || ['brouillon', 'documents_manquants'].includes(a.status))).forEach(a => out.push({
        key: `acc_${a.accident_id}`, prio: ['grave', 'critique'].includes(a.gravity) ? 1 : 3,
        plate: a.plate_at_accident, site: siteName(a.site_id),
        problem: `Accident incomplet — ${a.report_available !== 'disponible' ? 'constat manquant' : a.status}`,
        age: ageDays(a.created_at), owner: a.created_by?.name || '—',
        action: { label: "Ouvrir l'accident", run: () => onOpenAccident?.(a.accident_id) },
      }));
      // Contrôles
      (ctrls?.overdue || []).forEach(v => out.push({
        key: `ctl_${v.vehicle_id}`, prio: 2, plate: v.plate_number, site: siteName(v.site_id),
        problem: 'Contrôle hebdomadaire EN RETARD', age: null, owner: 'Admin de site',
        action: { label: 'Contrôler', run: () => setCheckVehicle(v) },
      }));
      (ctrls?.todo || []).forEach(v => out.push({
        key: `ctlt_${v.vehicle_id}`, prio: 4, plate: v.plate_number, site: siteName(v.site_id),
        problem: 'Contrôle hebdomadaire à réaliser', age: null, owner: 'Admin de site',
        action: { label: 'Contrôler', run: () => setCheckVehicle(v) },
      }));
      out.sort((a, b) => a.prio - b.prio || (b.age || 0) - (a.age || 0));
      setRows(out);
    } catch (e) { console.error(e); setRows([]); }
  };

  const returnToService = async (v) => {
    if (!window.confirm(`Remettre ${v.plate_number} en service ?`)) return;
    try {
      await axios.post(`${API}/fleet/vehicles/${v.vehicle_id}/return-to-service`, {}, { withCredentials: true });
      load(); onRefreshParc?.();
    } catch (e) { alert(e.response?.data?.detail || 'Refusé'); }
  };

  useEffect(() => { load(); }, [vehicles]);

  if (!rows) return <p className="text-sm text-gray-500">Chargement...</p>;
  return (
    <div className="space-y-2" data-testid="atraiter-tab">
      <p className="text-sm text-gray-600">{rows.length} élément(s) à traiter, triés par urgence.</p>
      {rows.length === 0 && (
        <Card><CardContent className="p-8 text-center text-gray-500">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />Rien à traiter 🎉
        </CardContent></Card>
      )}
      {rows.map(r => (
        <Card key={r.key} className="hover:border-blue-300" data-testid={`atraiter-row-${r.key}`}>
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded font-bold ${PRIO[r.prio].cls}`}>{PRIO[r.prio].label}</span>
            <span className="font-mono font-bold">{r.plate}</span>
            <span className="text-xs text-gray-500">{r.site}</span>
            <span className="text-sm flex-1 min-w-[200px]">{r.problem}</span>
            {r.age != null && <span className="text-xs text-gray-500">il y a {r.age} j</span>}
            <span className="text-xs text-gray-400">{r.owner}</span>
            {r.action?.run && (
              <Button size="sm" variant="outline" onClick={r.action.run} data-testid={`atraiter-action-${r.key}`}>{r.action.label}</Button>
            )}
          </CardContent>
        </Card>
      ))}
      <ChecklistDialog vehicle={checkVehicle} open={!!checkVehicle} onClose={() => setCheckVehicle(null)}
        onValidated={() => { load(); onRefreshParc?.(); }} />
      <IssueDetailDialog issueId={issueId} open={!!issueId} onClose={() => setIssueId(null)}
        isFleetManager={isFM} onChanged={() => { load(); onRefreshParc?.(); }} />
    </div>
  );
};

export default ATraiterTab;
